import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Mail, MessageSquare, Newspaper, LogOut, Home, 
  Trash2, Check, Plus, Edit, X, Save, Eye, EyeOff,
  ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [cast, setCast] = useState([]);
  const [news, setNews] = useState([]);

  // Form states
  const [showCastForm, setShowCastForm] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingCast, setEditingCast] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [castForm, setCastForm] = useState({ name: '', role: '', bio: '', image_url: '', is_main_cast: true, order: 0 });
  const [newsForm, setNewsForm] = useState({ title: '', content: '', category: 'announcement', image_url: '', is_published: true });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      const header = `Basic ${auth}`;
      await axios.post(`${API}/admin/login`, {}, { headers: { Authorization: header } });
      setAuthHeader(header);
      setIsLoggedIn(true);
      localStorage.setItem('tallawah_admin_auth', header);
    } catch (err) {
      setError('Invalid credentials');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAuthHeader('');
    localStorage.removeItem('tallawah_admin_auth');
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('tallawah_admin_auth');
    if (savedAuth) {
      setAuthHeader(savedAuth);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && authHeader) {
      fetchStats();
    }
  }, [isLoggedIn, authHeader]);

  useEffect(() => {
    if (isLoggedIn && authHeader) {
      if (activeTab === 'subscribers') fetchSubscribers();
      if (activeTab === 'contacts') fetchContacts();
      if (activeTab === 'inquiries') fetchInquiries();
      if (activeTab === 'cast') fetchCast();
      if (activeTab === 'news') fetchNews();
    }
  }, [activeTab, isLoggedIn, authHeader]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: { Authorization: authHeader } });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
    }
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers`, { headers: { Authorization: authHeader } });
      setSubscribers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: authHeader } });
      setContacts(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchInquiries = async () => {
    try {
      const res = await axios.get(`${API}/admin/inquiries`, { headers: { Authorization: authHeader } });
      setInquiries(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchCast = async () => {
    try {
      const res = await axios.get(`${API}/admin/cast`, { headers: { Authorization: authHeader } });
      setCast(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API}/admin/news`, { headers: { Authorization: authHeader } });
      setNews(res.data);
    } catch (err) { console.error(err); }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: { Authorization: authHeader } });
      fetchSubscribers();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const markContactRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/read`, {}, { headers: { Authorization: authHeader } });
      fetchContacts();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: { Authorization: authHeader } });
      fetchContacts();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const markInquiryRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}/read`, {}, { headers: { Authorization: authHeader } });
      fetchInquiries();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: { Authorization: authHeader } });
      fetchInquiries();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const saveCast = async (e) => {
    e.preventDefault();
    try {
      if (editingCast) {
        await axios.put(`${API}/admin/cast/${editingCast.id}`, castForm, { headers: { Authorization: authHeader } });
      } else {
        await axios.post(`${API}/admin/cast`, castForm, { headers: { Authorization: authHeader } });
      }
      setShowCastForm(false);
      setEditingCast(null);
      setCastForm({ name: '', role: '', bio: '', image_url: '', is_main_cast: true, order: 0 });
      fetchCast();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const deleteCast = async (id) => {
    if (!window.confirm('Delete this cast member?')) return;
    try {
      await axios.delete(`${API}/admin/cast/${id}`, { headers: { Authorization: authHeader } });
      fetchCast();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const saveNews = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await axios.put(`${API}/admin/news/${editingNews.id}`, newsForm, { headers: { Authorization: authHeader } });
      } else {
        await axios.post(`${API}/admin/news`, newsForm, { headers: { Authorization: authHeader } });
      }
      setShowNewsForm(false);
      setEditingNews(null);
      setNewsForm({ title: '', content: '', category: 'announcement', image_url: '', is_published: true });
      fetchNews();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Delete this news item?')) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`, { headers: { Authorization: authHeader } });
      fetchNews();
      fetchStats();
    } catch (err) { console.error(err); }
  };

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="bg-[#2A2A2A] p-8 rounded-lg w-full max-w-md border border-[#FEDD00]/30">
          <img 
            src="/tallawah-logo-gold.png" 
            alt="Tallawah Show" 
            className="h-24 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-[#FEDD00] text-center mb-6">Admin Panel</h1>
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded mb-4 flex items-center gap-2">
              <AlertCircle size={20} /> {error}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded focus:border-[#FEDD00] outline-none"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="w-full p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded focus:border-[#FEDD00] outline-none"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FEDD00] text-black font-bold py-3 rounded hover:bg-yellow-400 transition-colors"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <a href="/" className="block text-center text-gray-400 mt-4 hover:text-[#FEDD00]">
            ← Back to Website
          </a>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex">
      {/* Sidebar */}
      <div className="w-64 bg-[#0A0A0A] border-r border-[#FEDD00]/20 p-4">
        <img 
          src="/tallawah-logo-gold.png" 
          alt="Tallawah Show" 
          className="h-16 mx-auto mb-6"
        />
        <nav className="space-y-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: <Home size={20} /> },
            { id: 'subscribers', label: 'Subscribers', icon: <Mail size={20} /> },
            { id: 'contacts', label: 'Contacts', icon: <MessageSquare size={20} /> },
            { id: 'inquiries', label: 'Inquiries', icon: <MessageSquare size={20} /> },
            { id: 'cast', label: 'Cast', icon: <Users size={20} /> },
            { id: 'news', label: 'News', icon: <Newspaper size={20} /> },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                activeTab === item.id 
                  ? 'bg-[#FEDD00] text-black' 
                  : 'text-gray-400 hover:text-white hover:bg-[#2A2A2A]'
              }`}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </nav>
        <div className="mt-8 pt-4 border-t border-[#FEDD00]/20">
          <a href="/" className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-[#FEDD00]">
            <Home size={20} /> View Website
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300"
          >
            <LogOut size={20} /> Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        {/* Dashboard */}
        {activeTab === 'dashboard' && stats && (
          <div>
            <h1 className="text-3xl font-bold text-[#FEDD00] mb-8">Dashboard</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00]/20">
                <Mail className="text-[#FEDD00] mb-2" size={32} />
                <p className="text-3xl font-bold text-white">{stats.subscribers}</p>
                <p className="text-gray-400">Newsletter Subscribers</p>
              </div>
              <div className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00]/20">
                <MessageSquare className="text-[#FEDD00] mb-2" size={32} />
                <p className="text-3xl font-bold text-white">{stats.contacts.total}</p>
                <p className="text-gray-400">Contact Messages</p>
                {stats.contacts.unread > 0 && (
                  <span className="text-red-400 text-sm">{stats.contacts.unread} unread</span>
                )}
              </div>
              <div className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00]/20">
                <MessageSquare className="text-[#FEDD00] mb-2" size={32} />
                <p className="text-3xl font-bold text-white">{stats.inquiries.total}</p>
                <p className="text-gray-400">Support Inquiries</p>
                {stats.inquiries.unread > 0 && (
                  <span className="text-red-400 text-sm">{stats.inquiries.unread} unread</span>
                )}
              </div>
              <div className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00]/20">
                <Users className="text-[#FEDD00] mb-2" size={32} />
                <p className="text-3xl font-bold text-white">{stats.cast_members}</p>
                <p className="text-gray-400">Cast Members</p>
              </div>
            </div>
          </div>
        )}

        {/* Subscribers */}
        {activeTab === 'subscribers' && (
          <div>
            <h1 className="text-3xl font-bold text-[#FEDD00] mb-8">Newsletter Subscribers</h1>
            <div className="bg-[#2A2A2A] rounded-lg border border-[#FEDD00]/20 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1A1A1A]">
                  <tr>
                    <th className="text-left p-4 text-[#FEDD00]">Email</th>
                    <th className="text-left p-4 text-[#FEDD00]">Subscribed</th>
                    <th className="text-right p-4 text-[#FEDD00]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map(sub => (
                    <tr key={sub.id} className="border-t border-[#FEDD00]/10">
                      <td className="p-4 text-white">{sub.email}</td>
                      <td className="p-4 text-gray-400">{new Date(sub.subscribed_at).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => deleteSubscriber(sub.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {subscribers.length === 0 && (
                    <tr><td colSpan="3" className="p-8 text-center text-gray-500">No subscribers yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contacts */}
        {activeTab === 'contacts' && (
          <div>
            <h1 className="text-3xl font-bold text-[#FEDD00] mb-8">Contact Messages</h1>
            <div className="space-y-4">
              {contacts.map(contact => (
                <div key={contact.id} className={`bg-[#2A2A2A] p-6 rounded-lg border ${contact.is_read ? 'border-[#FEDD00]/10' : 'border-[#FEDD00]'}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">{contact.subject}</h3>
                      <p className="text-gray-400 text-sm">{contact.name} • {contact.email}</p>
                      <p className="text-gray-500 text-xs">{new Date(contact.submitted_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      {!contact.is_read && (
                        <button onClick={() => markContactRead(contact.id)} className="text-green-400 hover:text-green-300">
                          <Check size={18} />
                        </button>
                      )}
                      <button onClick={() => deleteContact(contact.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-300">{contact.message}</p>
                </div>
              ))}
              {contacts.length === 0 && (
                <div className="bg-[#2A2A2A] p-8 rounded-lg text-center text-gray-500">No contact messages yet</div>
              )}
            </div>
          </div>
        )}

        {/* Inquiries */}
        {activeTab === 'inquiries' && (
          <div>
            <h1 className="text-3xl font-bold text-[#FEDD00] mb-8">Support Inquiries</h1>
            <div className="space-y-4">
              {inquiries.map(inquiry => (
                <div key={inquiry.id} className={`bg-[#2A2A2A] p-6 rounded-lg border ${inquiry.is_read ? 'border-[#FEDD00]/10' : 'border-[#FEDD00]'}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="bg-[#FEDD00] text-black text-xs px-2 py-1 rounded">{inquiry.inquiry_type}</span>
                      <h3 className="text-lg font-bold text-white mt-2">{inquiry.name}</h3>
                      <p className="text-gray-400 text-sm">{inquiry.email} {inquiry.company && `• ${inquiry.company}`}</p>
                      <p className="text-gray-500 text-xs">{new Date(inquiry.submitted_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      {!inquiry.is_read && (
                        <button onClick={() => markInquiryRead(inquiry.id)} className="text-green-400 hover:text-green-300">
                          <Check size={18} />
                        </button>
                      )}
                      <button onClick={() => deleteInquiry(inquiry.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-300">{inquiry.message}</p>
                </div>
              ))}
              {inquiries.length === 0 && (
                <div className="bg-[#2A2A2A] p-8 rounded-lg text-center text-gray-500">No inquiries yet</div>
              )}
            </div>
          </div>
        )}

        {/* Cast */}
        {activeTab === 'cast' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-[#FEDD00]">Cast Members</h1>
              <button
                onClick={() => { setShowCastForm(true); setEditingCast(null); setCastForm({ name: '', role: '', bio: '', image_url: '', is_main_cast: true, order: 0 }); }}
                className="bg-[#FEDD00] text-black px-4 py-2 rounded flex items-center gap-2 hover:bg-yellow-400"
              >
                <Plus size={20} /> Add Cast Member
              </button>
            </div>

            {showCastForm && (
              <div className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00] mb-8">
                <h2 className="text-xl font-bold text-[#FEDD00] mb-4">{editingCast ? 'Edit' : 'Add'} Cast Member</h2>
                <form onSubmit={saveCast} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Name"
                      value={castForm.name}
                      onChange={(e) => setCastForm({ ...castForm, name: e.target.value })}
                      className="p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Role"
                      value={castForm.role}
                      onChange={(e) => setCastForm({ ...castForm, role: e.target.value })}
                      className="p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                      required
                    />
                  </div>
                  <textarea
                    placeholder="Bio"
                    value={castForm.bio}
                    onChange={(e) => setCastForm({ ...castForm, bio: e.target.value })}
                    className="w-full p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                    rows="3"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Image URL (optional)"
                    value={castForm.image_url}
                    onChange={(e) => setCastForm({ ...castForm, image_url: e.target.value })}
                    className="w-full p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                  />
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-gray-300">
                      <input
                        type="checkbox"
                        checked={castForm.is_main_cast}
                        onChange={(e) => setCastForm({ ...castForm, is_main_cast: e.target.checked })}
                        className="w-5 h-5"
                      />
                      Main Cast
                    </label>
                    <input
                      type="number"
                      placeholder="Order"
                      value={castForm.order}
                      onChange={(e) => setCastForm({ ...castForm, order: parseInt(e.target.value) || 0 })}
                      className="w-24 p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                    />
                  </div>
                  <div className="flex gap-4">
                    <button type="submit" className="bg-[#FEDD00] text-black px-6 py-2 rounded flex items-center gap-2">
                      <Save size={18} /> Save
                    </button>
                    <button type="button" onClick={() => setShowCastForm(false)} className="bg-gray-600 text-white px-6 py-2 rounded">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cast.map(member => (
                <div key={member.id} className="bg-[#2A2A2A] rounded-lg border border-[#FEDD00]/20 overflow-hidden">
                  <div className="h-48 bg-[#1A1A1A] flex items-center justify-center">
                    {member.image_url ? (
                      <img src={member.image_url} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <Users size={60} className="text-gray-600" />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-[#FEDD00]">{member.name}</h3>
                        <p className="text-gray-400 text-sm">{member.role}</p>
                        <span className={`text-xs ${member.is_main_cast ? 'text-green-400' : 'text-gray-500'}`}>
                          {member.is_main_cast ? 'Main Cast' : 'Supporting'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingCast(member); setCastForm(member); setShowCastForm(true); }}
                          className="text-[#FEDD00] hover:text-yellow-400"
                        >
                          <Edit size={18} />
                        </button>
                        <button onClick={() => deleteCast(member.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm mt-2 line-clamp-2">{member.bio}</p>
                  </div>
                </div>
              ))}
              {cast.length === 0 && (
                <div className="col-span-3 bg-[#2A2A2A] p-8 rounded-lg text-center text-gray-500">
                  No cast members added yet. Click "Add Cast Member" to get started.
                </div>
              )}
            </div>
          </div>
        )}

        {/* News */}
        {activeTab === 'news' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-[#FEDD00]">News & Press</h1>
              <button
                onClick={() => { setShowNewsForm(true); setEditingNews(null); setNewsForm({ title: '', content: '', category: 'announcement', image_url: '', is_published: true }); }}
                className="bg-[#FEDD00] text-black px-4 py-2 rounded flex items-center gap-2 hover:bg-yellow-400"
              >
                <Plus size={20} /> Add News
              </button>
            </div>

            {showNewsForm && (
              <div className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00] mb-8">
                <h2 className="text-xl font-bold text-[#FEDD00] mb-4">{editingNews ? 'Edit' : 'Add'} News Item</h2>
                <form onSubmit={saveNews} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Title"
                    value={newsForm.title}
                    onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                    className="w-full p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                    required
                  />
                  <textarea
                    placeholder="Content"
                    value={newsForm.content}
                    onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                    className="w-full p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                    rows="5"
                    required
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <select
                      value={newsForm.category}
                      onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                      className="p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                    >
                      <option value="announcement">Announcement</option>
                      <option value="press_release">Press Release</option>
                      <option value="media_coverage">Media Coverage</option>
                      <option value="feature">Feature</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Image URL (optional)"
                      value={newsForm.image_url}
                      onChange={(e) => setNewsForm({ ...newsForm, image_url: e.target.value })}
                      className="p-3 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={newsForm.is_published}
                      onChange={(e) => setNewsForm({ ...newsForm, is_published: e.target.checked })}
                      className="w-5 h-5"
                    />
                    Published
                  </label>
                  <div className="flex gap-4">
                    <button type="submit" className="bg-[#FEDD00] text-black px-6 py-2 rounded flex items-center gap-2">
                      <Save size={18} /> Save
                    </button>
                    <button type="button" onClick={() => setShowNewsForm(false)} className="bg-gray-600 text-white px-6 py-2 rounded">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-4">
              {news.map(item => (
                <div key={item.id} className="bg-[#2A2A2A] p-6 rounded-lg border border-[#FEDD00]/20">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#FEDD00] text-black text-xs px-2 py-1 rounded">{item.category}</span>
                        {!item.is_published && <span className="bg-gray-600 text-white text-xs px-2 py-1 rounded">Draft</span>}
                      </div>
                      <h3 className="text-xl font-bold text-white">{item.title}</h3>
                      <p className="text-gray-500 text-sm">{new Date(item.published_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingNews(item); setNewsForm(item); setShowNewsForm(true); }}
                        className="text-[#FEDD00] hover:text-yellow-400"
                      >
                        <Edit size={18} />
                      </button>
                      <button onClick={() => deleteNews(item.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-300 mt-4">{item.content}</p>
                </div>
              ))}
              {news.length === 0 && (
                <div className="bg-[#2A2A2A] p-8 rounded-lg text-center text-gray-500">
                  No news items yet. Click "Add News" to create your first post.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
